import { Cancel, DeleteForever, Edit, Save } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { gridClasses, GridRowModes } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { useMemo, useRef } from 'react';
import { appendWrapCellEditingClassName, DataGridStyles, wrapCellStyles } from '../../util/DataGridStyles';
import { numberWithCommas } from '../../util/JsonUtils';
import DateCell from '../DateCell';
import { EditableStringCell } from './EditableStringCell';
import { $$campaignVersionsDialog } from './model';
import { DataGrid, Tooltip } from '@ghs/components';
import { $$personaService } from '../../services/PersonaService/model.js';
import { $$app } from 'components/App/model.tsx';

/** @typedef {import('./model').GridRowId} GridRowId */

/**
 * CampaignVersionsTable
 *
 * @returns {React.ReactNode} - JSX Element
 */
export default function CampaignVersionsTable() {
  const dataGridProps = $$campaignVersionsDialog.$$versions.useDataGrid();

  const errors = useUnit($$campaignVersionsDialog.$$versions.$errors);
  const highlightedRows = useUnit($$campaignVersionsDialog.$$versions.$highlightedRows);
  const selectedRowIds = useUnit($$campaignVersionsDialog.$$versions.$selectedRowsIds);
  const unsavedChanges = useUnit($$campaignVersionsDialog.$$versions.$unsavedChanges);
  const isValidationPending = useUnit($$campaignVersionsDialog.$isValidationPending);
  const isSavePending = useUnit($$campaignVersionsDialog.$isSavePending);
  const isMapActivePersona = useUnit($$personaService.$isMapActivePersona);
  const userDetails = useUnit($$app.$userDetails);

  /**
   * Column definitions read live state through this ref instead of closing over the values above,
   * so `columns` can stay memoized (and its widths untouched by MUI) across renders that don't
   * change the column structure itself, e.g. entering/leaving edit mode.
   */
  const latestRef = useRef({ dataGridProps, errors, unsavedChanges, isValidationPending, isSavePending, isMapActivePersona, userDetails });
  latestRef.current = { dataGridProps, errors, unsavedChanges, isValidationPending, isSavePending, isMapActivePersona, userDetails };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>} */
  const dateColumnProps = {
    field: '',
    editable: true,
    flex: 1,
    valueGetter: params => dayjs(params).toDate(),
    valueFormatter: date => dayjs(date).format('MM/DD/YYYY'),
    renderCell: ({ row, id, field }) => {
      const latest = latestRef.current;
      return (
        <DateCell
          id={id}
          field={field}
          row={latest.dataGridProps.rows.find(r => r.id === id) || row}
          onValueChanged={$$campaignVersionsDialog.$$versions.cellValueChanged}
          errorMessage={latest.errors.find(e => e.id === id && e.field === field)?.error}
          cellMode="view"
        />
      );
    },
    renderEditCell: ({ row, id, field }) => {
      const latest = latestRef.current;
      return (
        <DateCell
          id={id}
          field={field}
          row={latest.dataGridProps.rows.find(r => r.id === id) || row}
          onValueChanged={$$campaignVersionsDialog.$$versions.cellValueChanged}
          errorMessage={latest.errors.find(e => e.id === id && e.field === field)?.error}
          cellMode="edit"
        />
      );
    },
    type: 'date'
  };

  const editableStringFieldMaxLengths = { name: 50, number: 20 };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>['renderCell']} */
  const renderCellWithValueTooltip = ({ value }) => (
    <Tooltip title={value ?? ''} arrow>
      <span style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>{value}</span>
    </Tooltip>
  );

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>} */
  const editableStringColumnProps = {
    field: '',
    editable: true,
    flex: 1,
    cellClassName: 'wrap-cell',
    renderCell: params => <EditableStringCell {...params} $$table={$$campaignVersionsDialog.$$versions} />,
    renderEditCell: params => <EditableStringCell {...params} $$table={$$campaignVersionsDialog.$$versions} inputProps={{ maxLength: editableStringFieldMaxLengths[params.field] }} />
  };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Version>[]} */
  const columns = useMemo(
    () => [
      { ...editableStringColumnProps, field: 'name', headerName: 'Version Name' },
      { ...editableStringColumnProps, field: 'number', headerName: 'Version Number' },
      ...(isMapActivePersona
        ? [
            {
              field: 'mspCode',
              headerName: 'MSP',
              flex: 1,
              renderCell: renderCellWithValueTooltip
            }
          ]
        : []),
      { ...dateColumnProps, field: 'firstMailDate', headerName: 'First Mail Date' },
      { ...dateColumnProps, field: 'lastMailDate', headerName: 'Last Mail Date' },
      { ...dateColumnProps, field: 'inHomeStartDate', headerName: 'In-Home Start' },
      { ...dateColumnProps, field: 'inHomeEndDate', headerName: 'In-Home End' },
      {
        field: 'mailClass',
        headerName: 'Mail Class',
        flex: 1,
        valueGetter: params => /** @type {Reseller.Version['mailClass']} */ (params)?.description || '',
        renderCell: renderCellWithValueTooltip
      },
      { field: 'mailQuantity', headerName: 'Mail Qty', flex: 1, valueGetter: numberWithCommas, renderCell: renderCellWithValueTooltip },
      {
        field: 'actions',
        type: 'actions',
        editable: false,
        headerName: 'Actions',
        headerAlign: 'center',
        getActions: ({ id }) => {
          const latest = latestRef.current;
          const isInEditMode = latest.dataGridProps.rowModesModel[id]?.mode === GridRowModes.Edit;
          const version = latest.dataGridProps.rows.find(row => row.id === id);
          const isCellWithError = latest.errors.some(e => e.id === id);
          const isRowHasUnsavedChanges = latest.unsavedChanges.find(r => r.id === id);
          if (!version) {
            return [];
          }
          const isVersionRemovable = !version.hasScans && (!latest.isMapActivePersona || latest.userDetails?.isInternal);
          return isInEditMode
            ? [
                <Tooltip key={`version-save-button-${id}`} title={!isCellWithError && (isRowHasUnsavedChanges ? 'Save' : 'No changes to save')} placement="bottom" arrow>
                  <span>
                    <IconButton key={`version-save-button-${id}`} disabled={isCellWithError || !isRowHasUnsavedChanges} onClick={() => $$campaignVersionsDialog.singleRowSaveClicked(id)}>
                      {latest.isValidationPending || latest.isSavePending ? <CircularProgress size={24} color="inherit" /> : <Save fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>,
                <IconButton key={`version-cancel-button-${id}`} onClick={() => $$campaignVersionsDialog.cancelClicked(id)}>
                  <Cancel fontSize="small" />
                </IconButton>
              ]
            : [
                <IconButton key={`version-edit-button-${id}`} onClick={() => $$campaignVersionsDialog.editClicked(id)}>
                  <Edit fontSize="small" color="primary" />
                </IconButton>,
                <Tooltip key={`version-delete-button-${id}`} title={version.hasScans ? 'Scans are associated with this version and it cannot be deleted' : 'Delete'} placement="bottom" arrow>
                  <span>
                    <IconButton onClick={() => $$campaignVersionsDialog.deleteSingleClicked(id)} disabled={!isVersionRemovable} sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}>
                      <DeleteForever fontSize="small" color={!isVersionRemovable ? /** @type {'secondary'} */ ('secondary.enabled') : 'warning'} />
                    </IconButton>
                  </span>
                </Tooltip>
              ];
        }
      }
    ],
    // Column structure only needs to change when the MSP column is toggled in/out; every other
    // value used inside the columns (errors, rowModesModel, etc.) is read live via `latestRef` so
    // that unrelated re-renders (e.g. entering edit mode) don't hand MUI a new `columns` reference
    // and cause it to re-derive (and reset) manually resized column widths.
    [isMapActivePersona]
  );

  return (
    <DataGrid
      data-testid="campaign-versions-table"
      {...dataGridProps}
      columns={columns}
      getRowHeight={() => 'auto'}
      onRowDoubleClick={({ id }) => $$campaignVersionsDialog.editClicked(id)}
      onCellDoubleClick={(_, event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        setTimeout(() => (target.tagName === 'INPUT' ? target : target.querySelector('input'))?.focus(), 0);
      }}
      pagination
      checkboxSelection={true}
      keepNonExistentRowsSelected={true}
      disableRowSelectionOnClick={true}
      pageSizeOptions={[5, 10, 25, 50]}
      autosizeOnMount={false} // vitest is failing beause of this prop
      autosizeOptions={{
        includeOutliers: true,
        includeHeaders: true
      }}
      initialState={{ columns: { columnVisibilityModel: { id: false } } }}
      getCellClassName={params => {
        const isEnteringEditMode = dataGridProps.rowModesModel[params.id]?.mode === GridRowModes.Edit && params.isEditable === true;
        const priorityClassName = Object.entries({
          [`${gridClasses.cell}--error`]: Boolean(errors.find(e => e.id === params.id && e.field === params.field)),
          'editable-date-cell': isEnteringEditMode,
          'editable-date': dataGridProps.rowModesModel[params.id]?.mode === GridRowModes.Edit && params.colDef.type === 'date',
          [`${gridClasses.cell}--edited`]: unsavedChanges.find(r => r.id === params.id && r.field === params.field),
          [`${gridClasses.cell}--success`]: highlightedRows.includes(params.id),
          [`${gridClasses.cell}--selected`]: selectedRowIds.includes(params.id)
        })
          .filter(([, value]) => value)
          .map(([key]) => key)
          .slice(0, 1)
          .join(' ');
        return appendWrapCellEditingClassName(priorityClassName, isEnteringEditMode);
      }}
      sx={{
        ...DataGridStyles,
        ...wrapCellStyles
      }}
    />
  );
}
